import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { schoolClassSortOrder } from '../services/TimetableService';
import './AdminHonorRollView.css';
import './StudentDetailPages.css';
import { scopeBySchool, schoolMatches } from '../utils/schoolScope';

function SuperAdminHonorRollView({ adminData }) {
  const [saSchoolId, setSaSchoolId] = useState('');
  const [saSchools, setSaSchools] = useState([]);
  useEffect(() => {
    getDocs(collection(db, 'schools')).then(snap => setSaSchools(snap.docs.map(d => ({ id: d.id, ...d.data() })))).catch(() => {});
  }, []);
  // Dropdown is authoritative: "All Schools" ('' ) = national view
  adminData = useMemo(() => ({ ...(adminData || {}), schoolId: saSchoolId || null }), [adminData, saSchoolId]);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [grades, setGrades] = useState([]);
  const [selectedClass, setSelectedClass] = useState('all');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadClasses = useCallback(async () => {
    console.log('🔥 AdminHonorRoll: Loading classes, schoolId:', adminData?.schoolId);
    try {
      // Case-insensitive school filter — iOS stores uppercase UUIDs
      const snapshot = await getDocs(collection(db, 'classes'));
      const classesData = scopeBySchool(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })), adminData?.schoolId);
      console.log('🔥 AdminHonorRoll: Classes loaded', classesData.length, classesData);
      classesData.sort(schoolClassSortOrder);
      setClasses(classesData);
    } catch (error) {
      console.error('🔥 AdminHonorRoll: Error loading classes:', error);
    }
  }, [adminData]);

  const loadStudents = useCallback(async () => {
    console.log('🔥 AdminHonorRoll: Loading students');
    try {
      const studentsData = [];
      for (const cls of classes) {
        const classStudentsQuery = query(
          collection(db, 'classes', cls.id, 'students'),
          where('isActive', '==', true)
        );
        const snapshot = await getDocs(classStudentsQuery);
        snapshot.docs.forEach(doc => {
          studentsData.push({ id: doc.id, ...doc.data() });
        });
      }
      console.log('🔥 AdminHonorRoll: Students loaded', studentsData.length, studentsData);
      setStudents(studentsData);
    } catch (error) {
      console.error('🔥 AdminHonorRoll: Error loading students:', error);
    } finally {
      setLoading(false);
    }
  }, [classes]);

  const loadGrades = useCallback(async () => {
    console.log('🔥 AdminHonorRoll: Loading grades');
    try {
      const gradesData = [];
      for (const cls of classes) {
        const assignmentsSnapshot = await getDocs(collection(db, 'classes', cls.id, 'assignments'));
        for (const assignmentDoc of assignmentsSnapshot.docs) {
          const gradesSnapshot = await getDocs(
            collection(db, 'classes', cls.id, 'assignments', assignmentDoc.id, 'grades')
          );
          gradesSnapshot.docs.forEach(doc => {
            gradesData.push({ id: doc.id, ...doc.data() });
          });
        }
      }
      console.log('🔥 AdminHonorRoll: Grades loaded', gradesData.length, gradesData);
      setGrades(gradesData);
    } catch (error) {
      console.error('🔥 AdminHonorRoll: Error loading grades:', error);
    }
  }, [classes]);

  useEffect(() => {
    loadClasses();
  }, [loadClasses]);

  useEffect(() => {
    if (classes.length > 0) {
      loadStudents();
      loadGrades();
    }
  }, [classes, loadStudents, loadGrades]);

  const calculateStudentAverage = (studentId) => {
    const studentGrades = grades.filter(grade => grade.studentId === studentId);
    if (studentGrades.length === 0) return 0;
    const sum = studentGrades.reduce((acc, grade) => acc + (grade.score / grade.maxScore) * 100, 0);
    return (sum / studentGrades.length).toFixed(1);
  };

  const getHonorRollStatus = (average) => {
    const avg = parseFloat(average);
    if (avg >= 95) return { status: 'Principal\'s List', color: '#dc2626', icon: '🏆' };
    if (avg >= 90) return { status: 'High Honor Roll', color: '#14532d', icon: '⭐' };
    if (avg >= 85) return { status: 'Honor Roll', color: '#22c55e', icon: '🌟' };
    return { status: 'Not Eligible', color: '#666', icon: '📝' };
  };

  const getFilteredStudents = () => {
    const studentsWithGrades = students.map(student => {
      const average = calculateStudentAverage(student.id);
      const honorRoll = getHonorRollStatus(average);
      return { ...student, average, honorRoll };
    });

    let filtered = studentsWithGrades;
    
    if (selectedClass !== 'all') {
      filtered = filtered.filter(student => student.classId === selectedClass);
    }

    return filtered.filter(student => student.honorRoll.status !== 'Not Eligible')
                     .sort((a, b) => b.average - a.average);
  };

  const getClassName = (classId) => {
    const cls = classes.find(c => c.id === classId);
    return cls ? cls.name : 'Unknown';
  };

  const getSchoolNameForClass = (classId) => {
    const cls = classes.find(c => c.id === classId);
    if (!cls || !cls.schoolId) return '';
    return saSchools.find(s => schoolMatches(s.id, cls.schoolId))?.name || '';
  };

  const createShareableContent = (student) => {
    const content = `
🏆 ACADEMIC HONOR ROLL 🏆
===============================

Student: ${student.fullName}
Student ID: ${student.studentId}
Class: ${getClassName(student.classId)}

Honor Roll Status: ${student.honorRoll.status}
Overall Average: ${student.average}%

Generated on ${new Date().toLocaleDateString()}
Generated by KN Education Portal
    `.trim();
    return content;
  };

  const handleShare = (student) => {
    const content = createShareableContent(student);
    if (navigator.share) {
      navigator.share({
        title: 'Honor Roll Certificate',
        text: content
      }).catch(console.error);
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(content).then(() => {
        alert('Honor roll details copied to clipboard!');
      }).catch(console.error);
    }
  };

  const handlePrint = (student) => {
    const content = createShareableContent(student);
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Honor Roll Certificate</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; line-height: 1.6; }
            pre { white-space: pre-wrap; font-family: Arial, sans-serif; }
          </style>
        </head>
        <body>
          <pre>${content}</pre>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  const honorRollStudents = getFilteredStudents();

  if (selectedStudent) {
    const st = selectedStudent;
    console.log('🔥 SuperAdminHonorRoll: Opened student detail', st.id);
    return (
      <div className="detail-page">
        <button className="detail-back-btn" onClick={() => setSelectedStudent(null)}>◄ Back</button>
        <div className="detail-hero">
          <div className="detail-hero-icon">{st.honorRoll.icon}</div>
          <div>
            <h2 className="detail-page-title">
              {st.fullName}
              <span className="detail-badge" style={{ background: `${st.honorRoll.color}20`, color: st.honorRoll.color }}>{st.honorRoll.status}</span>
            </h2>
            <p className="detail-page-subtitle">{getClassName(st.classId)}{getSchoolNameForClass(st.classId) ? ` · ${getSchoolNameForClass(st.classId)}` : ''}</p>
          </div>
        </div>

        <div className="detail-card">
          <h4>Honor Roll Details</h4>
          <div className="detail-rows">
            <div className="detail-row"><span className="detail-label">School</span><span className="detail-value">{getSchoolNameForClass(st.classId) || 'N/A'}</span></div>
            <div className="detail-row"><span className="detail-label">Student</span><span className="detail-value">{st.fullName}</span></div>
            <div className="detail-row"><span className="detail-label">Student ID</span><span className="detail-value">{st.studentId || 'N/A'}</span></div>
            <div className="detail-row"><span className="detail-label">Class</span><span className="detail-value">{getClassName(st.classId)}</span></div>
            <div className="detail-row"><span className="detail-label">Average</span><span className="detail-value">{st.average}%</span></div>
            <div className="detail-row"><span className="detail-label">Status</span><span className="detail-value">{st.honorRoll.status}</span></div>
          </div>
        </div>

        <div className="detail-actions">
          <button className="btn-primary" onClick={() => handleShare(st)}>Share</button>
          <button className="btn-secondary" onClick={() => handlePrint(st)}>Print</button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-honor-roll-view">
      <div className="view-header">
        <h2>Honor Roll</h2>
        <div className="sa-school-filter"><label>School:</label><select value={saSchoolId} onChange={(e) => setSaSchoolId(e.target.value)}><option value="">All Schools</option>{saSchools.map(s => <option key={s.id} value={s.id}>{s.name || s.id}</option>)}</select></div>
      </div>

      <div className="filter-bar">
        <select value={selectedClass} onChange={(e) => setSelectedClass(e.target.value)}>
          <option value="all">All Classes</option>
          {classes.map(cls => (
            <option key={cls.id} value={cls.id}>{cls.name}</option>
          ))}
        </select>
      </div>

      <div className="stats-section">
        <h3>Honor Roll Overview</h3>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">🏆</div>
            <div className="stat-info">
              <h3>{honorRollStudents.filter(s => s.honorRoll.status === 'Principal\'s List').length}</h3>
              <p>Principal's List</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">⭐</div>
            <div className="stat-info">
              <h3>{honorRollStudents.filter(s => s.honorRoll.status === 'High Honor Roll').length}</h3>
              <p>High Honor Roll</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">🌟</div>
            <div className="stat-info">
              <h3>{honorRollStudents.filter(s => s.honorRoll.status === 'Honor Roll').length}</h3>
              <p>Honor Roll</p>
            </div>
          </div>
        </div>
      </div>

      <p className="count-text">Total Honor Roll Students: {honorRollStudents.length}</p>

      {honorRollStudents.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">⭐</div>
          <h3>No Honor Roll Students</h3>
          <p>No students currently on honor roll</p>
        </div>
      ) : (
        <div className="honor-roll-list">
          {honorRollStudents.map(student => (
            <div key={student.id} className="honor-roll-card" style={{ cursor: 'pointer' }} onClick={() => setSelectedStudent(student)}>
              <div className="student-avatar">
                {student.firstName ? student.firstName[0] : 'S'}
              </div>
              <div className="student-info">
                <h4>{student.fullName}</h4>
                <p>Student ID: {student.studentId}</p>
                <p>Class: {getClassName(student.classId)}</p>
                {getSchoolNameForClass(student.classId) && <p>🏫 {getSchoolNameForClass(student.classId)}</p>}
              </div>
              <div className="honor-status">
                <span className="honor-icon">{student.honorRoll.icon}</span>
                <span 
                  className="honor-badge" 
                  style={{ background: `${student.honorRoll.color}20`, color: student.honorRoll.color }}
                >
                  {student.honorRoll.status}
                </span>
              </div>
              <div className="gpa-display">
                <span className="gpa-value">{student.average}%</span>
              </div>
              <div className="action-buttons">
                <button 
                  className="share-button"
                  onClick={(e) => { e.stopPropagation(); handleShare(student); }}
                  title="Share Honor Roll"
                >
                  Share
                </button>
                <button 
                  className="print-button"
                  onClick={(e) => { e.stopPropagation(); handlePrint(student); }}
                  title="Print Honor Roll"
                >
                  Print
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default SuperAdminHonorRollView;
